import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { cajaActual } from "../services/modules/Caja";
import { useAuth } from "./AuthContext";

const CAJA_REQUIRED_ROLES = new Set(["admin", "cajero"]);

const CajaContext = createContext(null);

const getSelectedCajaId = (fallback = 1) => {
  const raw = localStorage.getItem("selected_caja_id");
  const id = Number(raw);
  if (Number.isFinite(id) && id > 0) return id;
  return fallback;
};

export const CajaProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const role = user?.role || user?.rol;
  const cajaRequired = CAJA_REQUIRED_ROLES.has(role);

  const [sesion, setSesion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState(null);

  const refreshCaja = useCallback(async () => {
    if (!user?.isAuthenticated || !cajaRequired) {
      setSesion(null);
      setInitialized(true);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const id_caja = getSelectedCajaId(1);
      const data = await cajaActual({ id_caja });
      setSesion(data?.sesion || null);
    } catch (e) {
      setSesion(null);
      setError(e?.response?.data?.message || e?.message || "Error al validar caja.");
    } finally {
      setInitialized(true);
      setLoading(false);
    }
  }, [cajaRequired, user?.isAuthenticated]);

  useEffect(() => {
    if (authLoading) return;
    refreshCaja();
  }, [authLoading, refreshCaja]);

  const value = useMemo(() => {
    const cajaAbierta = Boolean(sesion?.id_caja_sesion);
    return { sesion, cajaAbierta, cajaRequired, loading, initialized, error, refreshCaja };
  }, [cajaRequired, error, initialized, loading, refreshCaja, sesion]);

  return <CajaContext.Provider value={value}>{children}</CajaContext.Provider>;
};

export const useCaja = () => {
  const ctx = useContext(CajaContext);
  if (!ctx) throw new Error("useCaja must be used within CajaProvider");
  return ctx;
};

